import { ButtonModule } from 'primeng/button';
import { OverlayBadgeModule } from 'primeng/overlaybadge';
import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { Role } from '../../../../auth/role.enum';
import { CatalogueProduct } from '../../../../../feature/shop/model/catalogue-product';
import { UserService } from '../../../../../common/service/user.service';
import { CartService } from '../../../../../feature/shop/cart.service';
import { CurrencyPipe, NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { PopoverModule } from 'primeng/popover';
import { RouterLink } from '@angular/router';
import { Constants } from '../../../../../common/helper/Constants';
import { Skeleton } from 'primeng/skeleton';
import { TranslationService } from '../../../../../common/service/translate.service';

@Component({
  selector: 'gate-shopping-cart-menu',
  standalone: true,
  imports: [ButtonModule, RouterLink, OverlayBadgeModule, PopoverModule, NgClass, TranslatePipe, CurrencyPipe, Skeleton],
  templateUrl: './shopping-cart-menu.component.html',
  styleUrl: './shopping-cart-menu.component.scss',
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShoppingCartMenuComponent implements OnInit {
  private readonly userService = inject(UserService);
  private readonly cartService = inject(CartService);
  public readonly locale = inject(TranslationService).currentLanguage;

  public route = Constants.routes;
  public role = Role;
  public cartProducts = signal<CatalogueProduct[]>([]);
  public customerId = signal<string | null>(null);
  public isCartLoading = signal<boolean>(false);
  public shoppingCartCount = signal<number>(0);
  public isDisabledBadge = signal<boolean>(true);
  public isCartPopoverVisible = signal<boolean>(false); // State to control Tailwind popover visibility
  @Input() mobileFullViewTrigger = false;
  @Input() inlinePanel = false;
  @Output() openCartFullView = new EventEmitter<void>();
  @Output() navigateToCart = new EventEmitter<void>();

  /**
   * Initializes the component and sets up the menu items.
   */
  public async ngOnInit() {
    // Wait for impersonation to complete before initializing
    await this.userService.waitForImpersonationToComplete();
    
    this.customerId.set(this.userService.getCustomerNumber());
    this.getItemCount();
    this.cartService.shoppingItemCountUpdate.subscribe(() => {
      this.getItemCount();
    });
  }

  getItemCount() {
    this.shoppingCartCount.set(0);
    this.cartProducts.set([]);
    if (this.checkPermissions()) {
      this.isCartLoading.set(true);
      this.isDisabledBadge.set(true);
      this.cartService.getCart(this.customerId()).then((cart) => {
        const items = (cart.items || []).map((item) => {
          item.numberOfItems = item.numberOfItems ?? 0;
          item.totalPrice = (item.aufpPrice2 ?? 0) * item.numberOfItems;
          item.isOutOfStock = item.aufpFrameMax <= 0;
          return item;
        });
        this.cartProducts.set(items);
        this.shoppingCartCount.set(cart.totalQuantity ?? 0);
        this.isDisabledBadge.set((cart.totalQuantity ?? 0) <= 0);
        this.isCartLoading.set(false);
      }).catch((error) => {
        this.isCartLoading.set(false);
        this.cartProducts.set([]);
        this.shoppingCartCount.set(0);
        this.isDisabledBadge.set(true);
      });
    }
  }

  public checkPermissions(): boolean {
    const groupRoles = this.userService.getRoles('group');
    return groupRoles.some((role) => [Role.GateEcommerceAdmin, Role.GateEcommerceOrderApprover, Role.GateEcommerceOrderRequester].includes(role));
  }

  onCartClick(event: Event) {
    if (!this.mobileFullViewTrigger) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.openCartFullView.emit();
  }

  onViewCartClick() {
    this.navigateToCart.emit();
  }

 getCartBadgeLabel(count: number): string {
  // 1000 and above → format as X.XK
  if (count >= 1000) {
    const kValue = (count / 1000).toFixed(1);

    // Remove .0 for clean numbers like 2000 → "2K"
    return kValue.endsWith('.0')
      ? `${parseInt(kValue)}K+`
      : `${kValue}K+`;
  }

  // 500–999 → step = 100 (keep 900 as 900+)
  if (count >= 500) {
    const bucket = Math.floor(count / 100) * 100;
    return `${bucket}+`;
  }

  // 0–499 → step = 50
  if (count >= 50) {
    const bucket = Math.floor(count / 50) * 50;
    return `${bucket}+`;
  }

  return count.toString();
}
}
